<template>
    <div>
        <div v-if="leaderboard" class="leaderboard-wizard-page">
            <div class="leaderboard-wizard-page__backdrop"></div>
            <div class="leaderboard-wizard-page__shell">
                <div class="leaderboard-wizard-page__topbar">
                    <button type="button" class="leaderboard-wizard-page__back" @click="closeLeaderboardWizard">
                        <span aria-hidden="true">&#8249;</span>
                        <strong>{{ LeaderBoardId ? 'Back to leaderboard' : 'Back to leaderboards' }}</strong>
                    </button>
                    <button type="button" class="leaderboard-wizard-page__close" aria-label="Close leaderboard wizard"
                        @click="closeLeaderboardWizard">
                        &times;
                    </button>
                </div>
                <div class="leaderboard-wizard">
                <header class="leaderboard-wizard__hero">
                    <div>
                        <p class="leaderboard-wizard__eyebrow">Affiliate competition</p>
                        <h2>{{ currentStepMeta.title }}</h2>
                        <p>{{ currentStepMeta.description }}</p>
                    </div>
                    <div class="leaderboard-wizard__summary">
                        <span>{{ leaderBoardStep }} of {{ wizardSteps.length }}</span>
                        <strong>{{ currentStepMeta.shortTitle }}</strong>
                    </div>
                </header>

                <nav class="leaderboard-wizard__steps" aria-label="Leaderboard setup steps">
                    <button
                        v-for="step in wizardSteps"
                        :key="step.number"
                        type="button"
                        class="leaderboard-wizard__step"
                        :class="{
                            'leaderboard-wizard__step--active': leaderBoardStep == step.number,
                            'leaderboard-wizard__step--complete': leaderBoardStep > step.number
                        }"
                        @click="goToLeaderboardStep(step.number)"
                    >
                        <span>{{ step.number }}</span>
                        <strong>{{ step.label }}</strong>
                    </button>
                </nav>

                <section class="leaderboard-wizard__body">
                    <div v-if="leaderBoardStep == 1" class="leaderboard-wizard__panel">
                        <div class="leaderboard-wizard__section-heading">
                            <div>
                                <h3>Leaderboard basics</h3>
                                <p>Name the competition, pick its funnel, and set the scoring window.</p>
                            </div>
                        </div>

                        <div class="leaderboard-wizard__field leaderboard-wizard__field--full">
                            <label>{{ $t('frontend_name') }}</label>
                            <text-input v-model="leaderboard.name" :attribute="$t('frontend_name')" value="leaderboard.name"
                                placeholder="Summer affiliate sprint" />
                        </div>

                        <div class="leaderboard-wizard__grid leaderboard-wizard__grid--two">
                            <div class="leaderboard-wizard__field">
                                <label>{{ $t('frontend_starting_date') }}</label>
                                <date-time-picker
                                    v-model="leaderboardDateRange.startDate"
                                    variant="base"
                                    :value="leaderboardDateRange.startDate"
                                />
                            </div>
                            <div class="leaderboard-wizard__field">
                                <label>{{ $t('frontend_ending_date') }}</label>
                                <date-time-picker
                                    v-model="leaderboardDateRange.endDate"
                                    variant="base"
                                    :value="leaderboardDateRange.endDate"
                                />
                            </div>
                        </div>

                        <div class="leaderboard-wizard__grid leaderboard-wizard__grid--three">
                            <div class="leaderboard-wizard__field">
                                <label>{{ $t('frontend_funnel') }}</label>
                                <gs-name-select
                                    v-model="leaderboard.funnel_id"
                                    size="full"
                                    max-height="180px"
                                    :placeholder="$t('frontend_funnels')"
                                    :options="leaderboardLiveFunnelsOptions"
                                />
                            </div>
                            <div class="leaderboard-wizard__field">
                                <label>{{ $t('frontend_no_of_aff') }}</label>
                                <gd-dropdown
                                    v-model="leaderboard.max_affiliates"
                                    size="full"
                                    max-height="180px"
                                    :options="rowSizeOptions"
                                />
                            </div>
                            <div class="leaderboard-wizard__field">
                                <label>{{ $t('frontend_groovesell_ranking_based_on') }}</label>
                                <button type="button" class="leaderboard-ranking-select" @click="showRankingPicker = true">
                                    <span>{{ selectedLeaderboardTypeLabel }}</span>
                                    <strong>Change</strong>
                                </button>
                            </div>
                        </div>

                        <div class="leaderboard-wizard__ranking-note">
                            <strong>{{ selectedLeaderboardTypeLabel }}</strong>
                            <p>{{ selectedLeaderboardTypeHelp }}</p>
                        </div>

                        <div class="leaderboard-wizard__callout">
                            <strong>Keep it focused.</strong>
                            Leaderboards are limited to a 90-day window so affiliates can track a live race instead of a stale report.
                        </div>
                    </div>

                    <div v-else-if="leaderBoardStep == 2" class="leaderboard-wizard__panel">
                        <div class="leaderboard-wizard__section-heading">
                            <div>
                                <h3>Eligible price points</h3>
                                <p>Choose exactly which products and price points can count toward this leaderboard.</p>
                            </div>
                            <div class="leaderboard-wizard__count-pill">{{ activePricePointCount }} enabled</div>
                        </div>

                        <div v-if="leaderboardProductsTabs.length" class="leaderboard-wizard__product-tabs">
                            <gd-tabs :tabs="leaderboardProductsTabs" :current-tab="leaderboardProductsTab"
                                :wrapper-class="'leaderboard-wizard__tabs'"
                                :tab-class="'leaderboard-wizard__tab'"
                                :tab-active-class="'leaderboard-wizard__tab--active'"
                                :line-class="'leaderboard-wizard__tab-line'"
                                @onClick="handleLeaderboardProductsTabChange" />
                        </div>

                        <div class="leaderboard-wizard__scroll-area">
                            <div v-for="(pricingPoint, index) in leaderboard.pricepoints" :key="index">
                                <div v-if="leaderboardProductsTab === 'leaderboard_product_' + pricingPoint.id"
                                    :ref="'leaderboard_product_' + pricingPoint.id">
                                    <div class="leaderboard-wizard__price-list">
                                        <article v-for="(pricePoint, pricePointIndex) in pricingPoint.price_points"
                                            :key="pricePointIndex"
                                            class="leaderboard-wizard__price-card"
                                            :class="{ 'leaderboard-wizard__price-card--active': pricePoint.enabled }">
                                            <div>
                                                <strong>{{ pricePoint.name }}</strong>
                                                <p>{{ pricePoint.enabled ? 'Sales from this price point count toward rankings.' : 'This price point is excluded from the leaderboard.' }}</p>
                                            </div>
                                            <gd-toggle v-model="pricePoint.enabled" :value="pricePoint.enabled" />
                                        </article>
                                    </div>
                                </div>
                            </div>
                            <div v-if="!leaderboardProductsTabs.length" class="leaderboard-wizard__empty">
                                <strong>No price points found</strong>
                                <p>Add products and price points to the selected funnel before building a leaderboard.</p>
                            </div>
                        </div>
                    </div>

                    <div v-else-if="leaderBoardStep == 3" class="leaderboard-wizard__panel">
                        <div class="leaderboard-wizard__section-heading">
                            <div>
                                <h3>Stats to display</h3>
                                <p>Pick the columns affiliates can see while they track the race.</p>
                            </div>
                            <div class="leaderboard-wizard__count-pill">{{ enabledStatsCount }} visible</div>
                        </div>

                        <div class="leaderboard-wizard__metric-grid">
                            <article
                                v-for="stat in statOptions"
                                :key="stat.key"
                                class="leaderboard-wizard__metric-card"
                                :class="{ 'leaderboard-wizard__metric-card--active': leaderboard.enabled_stats[stat.key] }"
                            >
                                <div>
                                    <strong>{{ stat.label }}</strong>
                                    <p>{{ stat.description }}</p>
                                </div>
                                <gd-toggle
                                    v-model="leaderboard.enabled_stats[stat.key]"
                                    :value="leaderboard.enabled_stats[stat.key]"
                                />
                            </article>
                        </div>
                    </div>

                    <div v-else-if="leaderBoardStep == 4" class="leaderboard-wizard__panel">
                        <div class="leaderboard-wizard__section-heading">
                            <div>
                                <h3>Prizes</h3>
                                <p>Add rewards for specific leaderboard positions. Prizes are optional, but they make the race clearer.</p>
                            </div>
                            <gd-button variant="primary" size="auto" @click="openPrizeEditor()">
                                <plus-icon class="w-5 h-5 fill-current" />
                                {{ $t('frontend_groovesell_add_prizes') }}
                            </gd-button>
                        </div>

                        <div v-if="leaderBordPricingData && leaderBordPricingData.length > 0" class="leaderboard-wizard__prize-list">
                            <article
                                v-for="(prize, index) in leaderBordPricingData"
                                :key="index"
                                class="leaderboard-wizard__prize-card"
                            >
                                <div class="leaderboard-wizard__prize-rank">#{{ prize.pricePositionOptionsValue }}</div>
                                <div class="leaderboard-wizard__prize-image">
                                    <img v-if="prize.prize_image" :src="prize.prize_image" alt="" />
                                    <span v-else>Prize</span>
                                </div>
                                <div class="leaderboard-wizard__prize-content">
                                    <strong>{{ prize.pricePointName }}</strong>
                                    <p>{{ formatPrizeValue(prize.pricePointValue) }}</p>
                                </div>
                                <div class="leaderboard-wizard__prize-actions">
                                    <gd-button variant="secondary" size="icon" @click="openPrizeEditor(prize, index)">
                                        <pencil-icon class="w-5 h-5 fill-current" />
                                    </gd-button>
                                    <gd-button variant="secondary" size="icon"
                                        @click="deletePrize(index + 1, prize, LeaderBoardId ? 'edit' : 'add')">
                                        <trash-icon class="w-5 h-5 fill-current" />
                                    </gd-button>
                                </div>
                            </article>
                        </div>
                        <div v-else class="leaderboard-wizard__empty leaderboard-wizard__empty--large">
                            <strong>No prizes yet</strong>
                            <p>{{ $t('frontend_groovesell_no_prizes_desc') }}</p>
                            <gd-button variant="primary" size="auto" @click="openPrizeEditor()">
                                <plus-icon class="w-5 h-5 fill-current" />
                                Add first prize
                            </gd-button>
                        </div>
                    </div>

                    <div v-else-if="leaderBoardStep == 5" class="leaderboard-wizard__panel">
                        <div class="leaderboard-wizard__section-heading">
                            <div>
                                <h3>Publish and embed</h3>
                                <p>Copy the hosted leaderboard link or embed it on a page.</p>
                            </div>
                        </div>

                        <div class="leaderboard-wizard__publish-grid">
                            <div class="leaderboard-wizard__publish-card">
                                <span>Hosted link</span>
                                <label>Share this URL with affiliates.</label>
                                <text-input v-model="leaderboardLink" class="w-full" :readonly="true" :select-all="true"
                                    value="leaderboardLink" placeholder="Link" />
                            </div>
                            <div class="leaderboard-wizard__publish-card leaderboard-wizard__publish-card--wide">
                                <span>{{ $t('frontend_affiliate_promo_embed_code') }}</span>
                                <label>Use this code on a Groove page or external site.</label>
                                <text-area v-model="leaderboard.leaderboard_embed" class="w-full" :readonly="true"
                                    :select-all="true" value="leaderboard.leaderboard_embed"
                                    :placeholder="$t('frontend_affiliate_promo_embed_code')" />
                            </div>
                        </div>
                    </div>
                </section>

                <footer class="leaderboard-wizard__footer">
                    <button v-if="leaderBoardStep > 1" type="button" class="leaderboard-wizard__ghost-button"
                        @click="goToLeaderboardStep(leaderBoardStep - 1)">Back</button>
                    <gd-button v-if="leaderBoardStep != 5" size="auto" variant="primary"
                        @click="saveAndNextOnLeaderboard">{{ $t('frontend_save_and_next') }}</gd-button>
                    <gd-button v-else size="auto" variant="primary" @click="closeLeaderboardWizard">{{
                        $t('frontend_close') }}</gd-button>
                </footer>
            </div>
            </div>
            <div v-if="showRankingPicker" class="leaderboard-ranking-picker" @click.self="showRankingPicker = false">
                <div class="leaderboard-ranking-picker__panel">
                    <header>
                        <div>
                            <span>Ranking calculation</span>
                            <h3>Choose how affiliate place is calculated</h3>
                            <p>These options decide what value determines 1st, 2nd, 3rd place and so on.</p>
                        </div>
                        <button type="button" aria-label="Close ranking chooser" @click="showRankingPicker = false">&times;</button>
                    </header>
                    <div class="leaderboard-ranking-picker__list">
                        <button
                            v-for="option in leaderboardTypeOptions"
                            :key="option.value"
                            type="button"
                            class="leaderboard-ranking-picker__option"
                            :class="{ 'leaderboard-ranking-picker__option--active': option.value == leaderboard.type }"
                            @click="selectLeaderboardType(option.value)"
                        >
                            <span>
                                <strong>{{ option.text }}</strong>
                                <small>{{ leaderboardTypeHelp[option.value] }}</small>
                            </span>
                            <em v-if="option.value == leaderboard.type">Selected</em>
                        </button>
                    </div>
                </div>
            </div>
            <div v-if="showPrizeEditor" class="leaderboard-prize-editor" @click.self="closePrizeEditor">
                <div class="leaderboard-prize-editor__panel">
                    <header>
                        <div>
                            <span>Leaderboard prize</span>
                            <h3>{{ editingPrizeIndex === null ? 'Add prize' : 'Edit prize' }}</h3>
                            <p>Set the reward, value, position, and optional thumbnail shown for a leaderboard rank.</p>
                        </div>
                        <button type="button" aria-label="Close prize editor" @click="closePrizeEditor">&times;</button>
                    </header>
                    <div class="leaderboard-prize-editor__body">
                        <div class="leaderboard-prize-editor__grid">
                            <div class="leaderboard-prize-editor__field">
                                <label>Prize name</label>
                                <text-input v-model="localPrize.pricePointName" attribute="Prize name" value="localPrize.pricePointName"
                                    placeholder="First place cash bonus" />
                            </div>
                            <div class="leaderboard-prize-editor__field">
                                <label>Prize value</label>
                                <gd-currency-input v-model="localPrize.pricePointValue" currency-code="USD" />
                            </div>
                        </div>
                        <div class="leaderboard-prize-editor__grid">
                            <div class="leaderboard-prize-editor__field">
                                <label>Leaderboard position</label>
                                <gd-dropdown
                                    v-model="localPrize.pricePositionOptionsValue"
                                    size="full"
                                    max-height="220px"
                                    :options="availablePrizePositions"
                                />
                            </div>
                            <div class="leaderboard-prize-editor__field">
                                <label>Prize thumbnail</label>
                                <image-picker v-model="localPrize.prize_image" :primary="true" />
                            </div>
                        </div>
                        <div v-if="prizeEditorError" class="leaderboard-prize-editor__error">{{ prizeEditorError }}</div>
                    </div>
                    <footer>
                        <button type="button" class="leaderboard-wizard__ghost-button" @click="closePrizeEditor">Cancel</button>
                        <gd-button variant="primary" size="auto" @click="savePrizeEditor">
                            {{ editingPrizeIndex === null ? 'Add prize' : 'Save prize' }}
                        </gd-button>
                    </footer>
                </div>
            </div>
        </div>
        <popup-alert v-if="alertProps.show" :variant="alertProps.variant" @close="alertProps.show = false">
            <template v-slot:title>
                {{ alertProps.title }}
            </template>
            <p>{{ alertProps.message }}</p>
        </popup-alert>
    </div>
</template>
<script>
import LeaderboardWidget from '@/models/LeaderboardWidget';
import Funnel from '@/models/Funnel';
import moment from 'moment-timezone';
import DateTimePicker from '@groovepages/gd-ui-kit/components/DateTimePicker';
import ImagePicker from '@groovepages/gd-ui-kit/components/ImagePickerModal';
import PencilIcon from '@/assets/images/icons/PencilIcon.svg';
import TrashIcon from '@/assets/images/icons/TrashIcon.svg';
import PlusIcon from '@/assets/images/icons/PlusIcon.svg';

export default {
    name: "SaveLeaderBoardModal",
    props: {
        openModal: {
            type: Function,
            required: true
        },
        editPrize: {
            type: Function,
            required: true
        },
        deletePrize: {
            type: Function,
            required: true
        },
        leaderBordPricingData: {
            type: Array,
            required: true
        },
        LeaderBoardId: {
            required: true,
            default: null
        },
    },
    components: {
        DateTimePicker,
        ImagePicker,
        PencilIcon,
        TrashIcon,
        PlusIcon,
    },

    data() {
        return {
            alertProps: {
                show: false,
                variant: 'success',
                title: '',
                message: '',
            },
            leaderBoardStep: 1,
            leaderboardFunnels: [],
            leaderboard: {},
            leaderboardDateRange: {
                startDate: moment().startOf('hour').toISOString(),
                endDate: moment().startOf('hour').add(30, 'days').toISOString(),
            },
            showRankingPicker: false,
            showPrizeEditor: false,
            editingPrizeIndex: null,
            prizeEditorError: '',
            localPrize: {
                pricePointName: '',
                pricePointValue: 0,
                pricePositionOptionsValue: '1',
                prize_image: '',
            },
            leaderboardLiveFunnels: [],
            leaderboardProductsTab: '',
            leaderBordPricingColumns: [
                'pricePointName',
                'pricePointValue',
                'pricePositionOptionsValue',
                'actions',
            ],
            leaderBordPrizingOptions: {
                headings: {
                    pricePointName: 'Name',
                    pricePointValue: 'Value',
                    pricePositionOptionsValue: 'Position',
                    actions: 'Actions',
                },
                perPage: 5,
                filterable: false,
                texts: {
                    filterPlaceholder: 'Search',
                    filter: '',
                },
            },
        }
    },
    async created() {
        this.$loader.start('fetchLeaderboardData');
        this.leaderboardFunnels = await Funnel.$get();
        this.leaderboardLiveFunnels = this.leaderboardFunnels.filter((x) => x.is_live);

        if (!this.LeaderBoardId) {
            this.leaderboard = new LeaderboardWidget({
                type: '1',
                name: '',
                funnel_id: this.leaderboardLiveFunnels[0].id, // Setting funnel_id from the first element of leaderboardFunnels array
                max_affiliates: 10,
                pricepoints: [],
                enabled_stats: [],
            });
        } else {
            this.leaderboard = await LeaderboardWidget.$find(this.LeaderBoardId);
            this.$emit('update-attributes', {
                key: 'leaderBordPricingData',
                value: this.leaderboard.leaderboard_config.prices
            })
            this.leaderboardDateRange = {
                startDate: new Date(this.leaderboard.start_timestamp * 1000).toISOString(),
                endDate: new Date(this.leaderboard.end_timestamp * 1000).toISOString(),
            };
            this.$emit('updatePricePositionOptions', this.leaderboard);
        }
        this.$emit('update-attributes', {
            key: 'leaderboard',
            value: this.leaderboard
        });
        this.$loader.end('fetchLeaderboardData');

    },
    computed: {
        leaderboardLiveFunnelsOptions() {
            return this.leaderboardLiveFunnels.map(funnel => ({ value: funnel.id, text: funnel.name }));
        },
        rowSizeOptions() {
            return [5, 10, 25, 50, 100];
        },
        leaderboardTypeOptions() {
            return [
                { value: '1', text: 'Gross revenue collected' },
                { value: '9', text: 'Projected revenue' },
                { value: '2', text: 'Gross commissions' },
                { value: '3', text: 'Net commissions after refunds' },
                { value: '4', text: 'Gross sales' },
                { value: '5', text: 'Net sales after refunds' },
                { value: '6', text: this.$t('frontend_signups') },
                { value: '7', text: this.$t('frontend_impressions') },
                { value: '8', text: this.$t('frontend_uniques') }
            ];
        },
        leaderboardTypeHelp() {
            return {
                1: 'Ranks affiliates by the total revenue collected before refund adjustments.',
                9: 'Ranks affiliates by projected revenue for payment plans and subscriptions.',
                2: 'Ranks affiliates by commissions earned before refunds are removed.',
                3: 'Ranks affiliates by commissions after refunded commissions are removed.',
                4: 'Ranks affiliates by total gross sales. Refunded sales still count in the ranking.',
                5: 'Ranks affiliates by net sales. Refunded sales are removed before ranking.',
                6: 'Ranks affiliates by the number of opt-ins or signups they generated.',
                7: 'Ranks affiliates by the number of impressions they generated.',
                8: 'Ranks affiliates by unique visitors generated.'
            };
        },
        selectedLeaderboardTypeOption() {
            return this.leaderboardTypeOptions.find((option) => option.value == this.leaderboard.type) || this.leaderboardTypeOptions[0];
        },
        selectedLeaderboardTypeLabel() {
            return this.selectedLeaderboardTypeOption ? this.selectedLeaderboardTypeOption.text : '';
        },
        selectedLeaderboardTypeHelp() {
            return this.leaderboardTypeHelp[this.leaderboard.type] || 'Choose the calculation used to decide affiliate placement.';
        },
        wizardSteps() {
            return [
                {
                    number: 1,
                    label: 'Basics',
                    shortTitle: 'Basics',
                    title: this.LeaderBoardId ? 'Edit leaderboard' : 'Create leaderboard',
                    description: 'Set the name, funnel, leaderboard size, date range, and how the winner is calculated.'
                },
                {
                    number: 2,
                    label: 'Products',
                    shortTitle: 'Price points',
                    title: 'Choose price points',
                    description: 'Control which products and price points are eligible for this competition.'
                },
                {
                    number: 3,
                    label: 'Stats',
                    shortTitle: 'Visible stats',
                    title: 'Choose visible stats',
                    description: 'Decide which performance columns affiliates will see on the leaderboard.'
                },
                {
                    number: 4,
                    label: 'Prizes',
                    shortTitle: 'Prizes',
                    title: 'Add leaderboard prizes',
                    description: 'Reward specific leaderboard positions with prizes affiliates can understand at a glance.'
                },
                {
                    number: 5,
                    label: 'Publish',
                    shortTitle: 'Publish',
                    title: 'Publish the leaderboard',
                    description: 'Copy the public leaderboard link or embed code once the competition is ready.'
                }
            ];
        },
        currentStepMeta() {
            return this.wizardSteps.find((step) => step.number == this.leaderBoardStep) || this.wizardSteps[0];
        },
        statOptions() {
            return [
                { key: 'impressions', label: this.$t('frontend_impressions'), description: 'How many times affiliate links were shown.' },
                { key: 'uniques', label: this.$t('frontend_uniques'), description: 'Unique visitors driven by each affiliate.' },
                { key: 'optins', label: this.$t('frontend_signups'), description: 'Opt-ins or signups generated by each affiliate.' },
                { key: 'sales', label: this.$t('frontend_sales'), description: 'Number of sales credited to each affiliate.' },
                { key: 'total_sales_amount', label: this.$t('frontend_groovesell_total_sales_amount'), description: 'Total gross sales amount before refunds are removed.' },
                { key: 'projected_revenue', label: this.$t('frontend_groovesell_lb_projected_sales_amount'), description: 'Projected revenue from plans or subscriptions.' },
                { key: 'refunds', label: this.$t('frontend_refunds'), description: 'Number of refunded purchases.' },
                { key: 'total_refunds_amount', label: this.$t('frontend_refunds_amount'), description: 'Total dollar amount refunded.' },
                { key: 'earned_commissions', label: this.$t('frontend_groovesell_earned_commissions'), description: 'Commissions earned before refund adjustments.' },
                { key: 'refunded_commissions', label: this.$t('frontend_groovesell_lb_refund_commissions'), description: 'Commissions removed because of refunds.' },
                { key: 'commissions_without_refunds', label: this.$t('frontend_groovesell_lb_commissions_without_refunds'), description: 'Net commissions after refunds are removed.' }
            ];
        },
        enabledStatsCount() {
            if (!this.leaderboard.enabled_stats) return 0;
            return this.statOptions.filter((stat) => this.leaderboard.enabled_stats[stat.key]).length;
        },
        activePricePointCount() {
            if (!this.leaderboard.pricepoints) return 0;
            return this.leaderboard.pricepoints.reduce((total, product) => {
                return total + product.price_points.filter((pricePoint) => pricePoint.enabled).length;
            }, 0);
        },
        availablePrizePositions() {
            const usedPositions = this.leaderBordPricingData
                .filter((prize, index) => index !== this.editingPrizeIndex)
                .map((prize) => prize.pricePositionOptionsValue && prize.pricePositionOptionsValue.toString());
            const positions = [];
            const max = Number(this.leaderboard.max_affiliates || 10);
            for (let index = 1; index <= max; index++) {
                if (!usedPositions.includes(index.toString())) {
                    positions.push({
                        value: index.toString(),
                        text: `Position #${index}`,
                    });
                }
            }
            return positions;
        },
        leaderboardLink() {
            if (this.leaderboard && this.leaderboard.permalink) {
                const site_appurl = window.config.appUrl.replace(/^https?:\/\//i, '');
                return `https://leaderboards.${site_appurl}/leaderboard/${this.leaderboard.permalink}`;
            }
            return '';
        },
        leaderboardProductsTabs() {
            const leaderboardProductsTabs = [];
            for (let i = 0; i < this.leaderboard.pricepoints.length; i++) {
                leaderboardProductsTabs.push({
                    title: this.leaderboard.pricepoints[i].name,
                    value: `leaderboard_product_${this.leaderboard.pricepoints[i].id}`,
                });
            }
            return leaderboardProductsTabs;
        },
    },
    methods: {
        goToLeaderboardStep(step) {
            if (step < 1 || step > this.wizardSteps.length) return;
            this.leaderBoardStep = step;
        },
        selectLeaderboardType(value) {
            this.leaderboard.type = value;
            this.showRankingPicker = false;
        },
        openPrizeEditor(prize = null, index = null) {
            this.editingPrizeIndex = index;
            this.prizeEditorError = '';
            this.localPrize = prize ? {
                pricePointName: prize.pricePointName || '',
                pricePointValue: prize.pricePointValue || 0,
                pricePositionOptionsValue: prize.pricePositionOptionsValue ? prize.pricePositionOptionsValue.toString() : '1',
                prize_image: prize.prize_image || '',
            } : {
                pricePointName: '',
                pricePointValue: 0,
                pricePositionOptionsValue: this.availablePrizePositions[0] ? this.availablePrizePositions[0].value : '1',
                prize_image: '',
            };
            this.showPrizeEditor = true;
        },
        closePrizeEditor() {
            this.showPrizeEditor = false;
            this.editingPrizeIndex = null;
            this.prizeEditorError = '';
        },
        savePrizeEditor() {
            if (!this.localPrize.pricePointName || this.localPrize.pricePointName.trim().length < 3) {
                this.prizeEditorError = 'Enter a prize name with at least 3 characters.';
                return;
            }
            if (!this.localPrize.pricePositionOptionsValue) {
                this.prizeEditorError = 'Choose the leaderboard position this prize belongs to.';
                return;
            }

            const nextPrize = {
                pricePointName: this.localPrize.pricePointName,
                pricePointValue: this.localPrize.pricePointValue,
                pricePositionOptionsValue: this.localPrize.pricePositionOptionsValue,
                prize_image: this.localPrize.prize_image,
            };
            const nextPrizes = [...this.leaderBordPricingData];
            if (this.editingPrizeIndex === null) {
                nextPrizes.push(nextPrize);
            } else {
                nextPrizes.splice(this.editingPrizeIndex, 1, nextPrize);
            }
            nextPrizes.sort((a, b) => Number(a.pricePositionOptionsValue) - Number(b.pricePositionOptionsValue));
            this.$emit('update-attributes', {
                key: 'leaderBordPricingData',
                value: nextPrizes,
            });
            this.closePrizeEditor();
        },
        formatPrizeValue(value) {
            const amount = Number(value || 0);
            return amount.toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2,
            });
        },
        async saveAndNextOnLeaderboard() {
            if (!this.leaderboard.name || this.leaderboard.name == ''){
                this.alertProps = {
                    show: true,
                    variant: 'error',
                    title: this.$t('frontend_error'),
                    message: this.$t('frontend_groovesell_swal_valid_name_for_leaderboard'),
                };
                return;
            }
            if (moment(this.leaderboardDateRange.endDate).diff(moment(this.leaderboardDateRange.startDate)) <= 0) {
                this.alertProps = {
                    show: true,
                    variant: 'error',
                    title: this.$t('frontend_error'),
                    message: this.$t('frontend_groovesell_swal_select_valid_date_range'),
                };
                return;
            }
            if (moment(this.leaderboardDateRange.endDate).diff(moment(this.leaderboardDateRange.startDate), 'days') > 90) {
                this.alertProps = {
                    show: true,
                    variant: 'error',
                    title: this.$t('frontend_error'),
                    message: this.$t('frontend_groovesell_swal_leaderboard_date_range'),
                };
                return;
            }

            this.leaderboard.start_at = moment(this.leaderboardDateRange.startDate).tz('America/New_York').format('MM/DD/YYYY hh:mm A');
            this.leaderboard.end_at = moment(this.leaderboardDateRange.endDate).tz('America/New_York').format('MM/DD/YYYY hh:mm A');
            this.$loader.start('saveAndNextOnLeaderboard');
            if (this.leaderBoardStep == 1) {
                const savedLeaderboard = await this.leaderboard.save();
                this.$emit('refreshLeaderboardData')
                this.leaderboard = await LeaderboardWidget.$find(savedLeaderboard.data.id);
                this.leaderboardProductsTab = `leaderboard_product_${this.leaderboard.pricepoints[0].id}`;
                this.leaderBoardStep = 2;
                this.$emit('updatePricePositionOptions', this.leaderboard);
            } else if (this.leaderBoardStep == 2) {
                const savedLeaderboard = await this.leaderboard.save();
                this.leaderboard = await LeaderboardWidget.$find(savedLeaderboard.data.id);
                this.leaderBoardStep = 3;
            } else if (this.leaderBoardStep == 3) {
                const savedLeaderboard = await this.leaderboard.save();
                this.leaderboard = await LeaderboardWidget.$find(savedLeaderboard.data.id);
                this.leaderBoardStep = 4;
            } else if (this.leaderBoardStep == 4) {
                this.leaderboard.leaderboard_config = {
                    prices: this.leaderBordPricingData.length
                        ? this.leaderBordPricingData
                        : [],
                    teams: this.leaderboard.leaderboard_config.teams.length
                        ? this.leaderboard.leaderboard_config.teams
                        : [],
                };
                const savedLeaderboard = await this.leaderboard.save(); // update data
                this.leaderboard = await LeaderboardWidget.$find(savedLeaderboard.data.id);
                this.leaderBoardStep = 5;
            }
            this.$emit('update-attributes', {
                key: 'leaderboard',
                value: this.leaderboard
            });
            this.$loader.end('saveAndNextOnLeaderboard');
        },
        async closeLeaderboardWizard() {
            if (!this.LeaderBoardId) this.$emit('refreshLeaderboardData');
            this.$root.$emit('modal-close', 'leaderboards-modal');
            this.$emit('update-attributes', {
                key: 'showSaveLeaderBoardModal',
                value: false,
            });
        },
        handleLeaderboardProductsTabChange(newTab) {
            this.leaderboardProductsTab = newTab;
        },
        generateDynamicOptionArr(leaderBoardData, ignore_position = null) {

            const mynumber = leaderBoardData.max_affiliates;
            const leaderBaordPriceData = leaderBoardData.leaderboard_config.prices;

            const ignoreArr = [];
            for (let i = 0; i < leaderBaordPriceData.length; i++) {
                if (ignore_position != null && ignore_position == leaderBaordPriceData[i].pricePositionOptionsValue) {
                    continue;
                }
                ignoreArr.push(leaderBaordPriceData[i].pricePositionOptionsValue);
            }

            const arr = [];
            for (let i = 1; i <= mynumber; i++) {
                if (!ignoreArr.includes(i.toString())) {
                    arr.push(i.toString());
                }
            }

            arr.sort((a, b) => a - b);

            return arr;
        }
    }

}
</script>
